import React from "react"
import {
  IconAbc,
  IconVolume,
  IconVocabulary,
  IconArrowsSort,
  IconListNumbers,
  IconReplace,
  IconLetterCase,
  IconFeather,
  IconHeart,
} from "@tabler/icons-react"
import type { ReactNode } from "react"
import type {
  GrammarEntry,
  GrammarSlide,
  GrammarSlideNav,
} from "./grammar_glossary"
import { GRAMMAR_GLOSSARY, FIELD_TO_GLOSSARY } from "./grammar_glossary"
import { pronunciationGuide } from "./pronunciation_guide"

const ICON_SIZE = 20
const NAV_ICON_SIZE = 18

// The top-level pages of the Irish language reference. Exported so callers open the modal to a
// specific page by key, e.g. `<IrishLanguageReference page={PAGES.PRONUNCIATION}>`.
export const PAGES = {
  HOME: "HOME",
  SPELLING: "SPELLING",
  PRONUNCIATION: "PRONUNCIATION",
  GRAMMAR: "GRAMMAR",
  VSO: "VSO",
  CONJUGATION: "CONJUGATION",
  OGHAM: "OGHAM",
  CLOSING: "CLOSING",
} as const

export type PageKey = keyof typeof PAGES
export type SectionKey = Exclude<PageKey, "HOME">

// --- Section content authored here (short reference entries), reusing the richer guides where they
// already exist (pronunciation, conjugation). Each entry is paged: one heading per page. ---

const spellingGuide: GrammarEntry = {
  title: "Spelling",
  singlePage: true,
  slides: [
    {
      heading: "The 18-letter alphabet",
      body: "Irish spells its sounds with 18 letters: a b c d e f g h i l m n o p r s t u. There's no j k q v w x y z (they turn up only in loanwords). An 'h' usually marks a sound change on the letter before it rather than being a letter in its own right.",
      examples: [{ irish: "a b c d e f g h i l m n o p r s t u", english: "the traditional aibítir" }],
    },
    {
      heading: "Fadas make vowels long",
      tip: "fada look tiny but change whole word. grug always type fada. no fada, wrong word, sad grug.",
      body: "The síneadh fada (the accent, á é í ó ú) makes a vowel long, and it can change the word entirely. It's never optional: always type it.",
      examples: [
        { irish: "Seán vs sean", english: "John vs old" },
        { irish: "fáilte vs failte", english: "welcome vs (nothing)" },
      ],
    },
    {
      heading: "Broad and slender",
      body: "A consonant is 'broad' beside a, o, u and 'slender' beside e, i, and it sounds different in each. The rule caol le caol, leathan le leathan ('slender with slender, broad with broad') means the vowels either side of a consonant must match in type.",
      examples: [{ irish: "bord (broad) vs beir (slender)", english: "table vs carry" }],
    },
  ],
}

// Pull a glossary entry's slides into the Grammar chapter, tagging its first slide with a jump-to nav
// card. One source of truth: these are the same entries the dictionary's inline grammar terms open.
const grammarPointSlides = (key: string, nav: GrammarSlideNav): GrammarSlide[] => {
  const entry = GRAMMAR_GLOSSARY[key]
  if (!entry) return []
  return entry.slides.map((slide, index) => (index === 0 ? { ...slide, nav } : slide))
}

// The Grammar chapter's fixed intro pages (overview + initial mutations).
const GRAMMAR_INTRO: GrammarSlide[] = [
  {
    heading: "What surprises English speakers",
    tip: "grug see three scary thing at once and freeze. grug learn one at a time. small step good, big leap fall down.",
    body: "Three things stand out: the verb comes first (VSO word order), the start of a word can change (initial mutations), and there's no verb 'to have' (you say something is 'at' you). Jump to a topic below, or page through.",
    examples: [{ irish: "Tá Gaeilge agam", english: "I speak Irish (lit. Irish is at me)" }],
  },
  {
    heading: "Initial mutations",
    body: "After certain words the first letter softens (séimhiú, an added h) or is eclipsed (urú, a prefixed letter). The pronunciation guide covers how each one sounds.",
    examples: [{ irish: "bord → a bhord → ár mbord", english: "table → his table → our table" }],
    nav: {
      label: "Initial mutations",
      description: "The start of a word softens or is eclipsed.",
      example: "bord → mbord",
      icon: <IconReplace size={NAV_ICON_SIZE} />,
    },
  },
]

// The per-point grammar entries, in order. Each contributes its glossary slides (the same content as
// the dictionary's inline tooltip); the `key` also lets a grammar term deep-link to its page.
const GRAMMAR_POINTS: { key: string; nav: GrammarSlideNav }[] = [
  { key: "gender", nav: { label: "Gender", description: "Masculine or feminine, changing nearby words.", example: "an cat / an bhean", icon: <IconVocabulary size={NAV_ICON_SIZE} /> } },
  { key: "genitive", nav: { label: "The genitive (of)", description: "The 'of' form of a noun.", example: "doras an tí", icon: <IconArrowsSort size={NAV_ICON_SIZE} /> } },
  { key: "declension", nav: { label: "Declensions", description: "The five noun patterns.", example: "1st–5th", icon: <IconListNumbers size={NAV_ICON_SIZE} /> } },
  { key: "plurals", nav: { label: "Plurals", description: "How a noun forms its plural.", example: "cat → cait", icon: <IconLetterCase size={NAV_ICON_SIZE} /> } },
  { key: "verbal_noun", nav: { label: "Verbal noun", description: "The '-ing' form used as a noun.", example: "ag rith", icon: <IconAbc size={NAV_ICON_SIZE} /> } },
  { key: "verbal_adjective", nav: { label: "Verbal adjective", description: "The past-participle '-ed' form.", example: "rite", icon: <IconFeather size={NAV_ICON_SIZE} /> } },
]

// Assemble the chapter and record where each point starts, so a dictionary grammar term can open the
// modal straight to its page and the reader can keep paging from there.
const grammarSlides: GrammarSlide[] = [...GRAMMAR_INTRO]
const GRAMMAR_PAGE_OF: Record<string, number> = {}
GRAMMAR_POINTS.forEach(({ key, nav }) => {
  GRAMMAR_PAGE_OF[key] = grammarSlides.length
  grammarSlides.push(...grammarPointSlides(key, nav))
})

const grammarChapter: GrammarEntry = {
  title: "Grammar essentials",
  slides: grammarSlides,
}

/**
 * Where a dictionary grammar field key (gender, genitive_singular, conjugation_class, ...) lives in the
 * language modal, so its inline term can open the guide there and let the reader keep paging. Undefined
 * for terms not covered by a chapter (they fall back to the short inline explainer).
 */
export const grammarTermTarget = (
  fieldKey?: string | null,
): { page: PageKey; chapterPage: number } | undefined => {
  if (!fieldKey) return undefined
  const glossaryKey = FIELD_TO_GLOSSARY[fieldKey] ?? fieldKey
  if (glossaryKey in GRAMMAR_PAGE_OF) {
    return { page: PAGES.GRAMMAR, chapterPage: GRAMMAR_PAGE_OF[glossaryKey] }
  }
  if (glossaryKey === "conjugation") return { page: PAGES.CONJUGATION, chapterPage: 0 }
  return undefined
}

const wordOrderGuide: GrammarEntry = {
  title: "Word order (VSO)",
  singlePage: true,
  slides: [
    {
      heading: "Verb, then subject, then object",
      tip: "grug not translate word by word from english. grug put verb first and trust it. head catch up fast.",
      body: "Irish is a VSO language: the verb comes first, then who does it, then what. English is SVO ('the cat drinks milk'); Irish puts the verb up front.",
      examples: [{ irish: "Ólann an cat bainne", english: "The cat drinks milk (lit. Drinks the cat milk)" }],
    },
    {
      heading: "Questions and negatives",
      body: "A particle goes before the verb: an for a yes/no question, ní for a negative. The verb-first order doesn't change.",
      examples: [
        { irish: "An ólann an cat bainne?", english: "Does the cat drink milk?" },
        { irish: "Ní ólann an cat bainne", english: "The cat doesn't drink milk" },
      ],
    },
    {
      heading: "No single word for yes or no",
      body: "You answer by echoing the verb, positive or negative. There's no bare 'yes' or 'no'.",
      examples: [{ irish: "An dtuigeann tú? Tuigim / Ní thuigim", english: "Do you understand? I understand / I don't" }],
    },
  ],
}

// A short sign-off: the guide is a living, community-corrected work in progress.
const closingNote: GrammarEntry = {
  title: "Sin é!",
  singlePage: true,
  slides: [
    {
      heading: "Sin é! (that's it)",
      body: "That's the tour. We're building resources like this as we learn the language ourselves, so this guide grows over time. Corrections are always welcome, and we'll keep making updates, so be sure to check back often. Go raibh maith agat for your support!",
      examples: [
        { irish: "Sin é!", english: "That's it!" },
        { irish: "Go raibh maith agat", english: "Thank you (for your support)" },
      ],
    },
  ],
}

// A short cultural aside: the ancient Ogham alphabet. Codex list items render each record's name in
// Ogham, so this section explains what the reader is looking at.
const oghamGuide: GrammarEntry = {
  title: "Ogham",
  singlePage: true,
  slides: [
    {
      heading: "An ancient Celtic alphabet",
      body: "Ogham is the earliest form of writing for the Irish language, used mainly between the 4th and 6th centuries AD. Around 400 stone inscriptions survive, most of them in Ireland, usually carved along the edge of a standing stone to record a name.",
      examples: [{ irish: "ᚑᚌᚐᚋ", english: "the word 'Ogam' itself" }],
    },
    {
      heading: "Read it along a line",
      tip: "grug find the spine first, then count the scratches. up the stone, not across. easy once grug see it.",
      body: "Each letter is a group of one to five strokes set across, through, or beside a central stem line (the droim). On a stone you read from the bottom up; in a line, from left to right.",
      examples: [{ irish: "ᚁ ᚂ ᚃ ᚄ ᚅ", english: "one to five strokes: b l f s n" }],
    },
    {
      heading: "The four aicmí",
      body: "The twenty core letters are grouped into four families (aicmí), each named after its first letter: Beithe, hÚatha, Muine, and Ailme. Each letter also carries a traditional tree or plant name, which is why Ogham is nicknamed the 'tree alphabet'.",
      examples: [{ irish: "ᚁ ᚆ ᚋ ᚐ", english: "the four aicmí start with b, h, m, a" }],
    },
  ],
}

export interface LanguageSection {
  key: SectionKey
  label: string
  description: string
  icon: ReactNode
  entry: GrammarEntry
}

// The home-screen sections, in order. Each renders as a paged guide; CONJUGATION reuses the richer
// conjugation entry (one conjugation pattern per page).
export const LANGUAGE_SECTIONS: LanguageSection[] = [
  {
    key: "SPELLING",
    label: "Spelling",
    description: "The alphabet, fadas, broad and slender.",
    icon: <IconAbc size={ICON_SIZE} />,
    entry: spellingGuide,
  },
  {
    key: "PRONUNCIATION",
    label: "Pronunciation",
    description: "How the spelling sounds, and the mutations.",
    icon: <IconVolume size={ICON_SIZE} />,
    entry: pronunciationGuide,
  },
  {
    key: "GRAMMAR",
    label: "Grammar",
    description: "Gender, mutations, and cases.",
    icon: <IconVocabulary size={ICON_SIZE} />,
    entry: grammarChapter,
  },
  {
    key: "VSO",
    label: "Word order",
    description: "Verb-subject-object, questions, yes and no.",
    icon: <IconArrowsSort size={ICON_SIZE} />,
    entry: wordOrderGuide,
  },
  {
    key: "CONJUGATION",
    label: "Verb conjugation",
    description: "How verbs change for tense and person.",
    icon: <IconListNumbers size={ICON_SIZE} />,
    entry: GRAMMAR_GLOSSARY.conjugation,
  },
  {
    key: "OGHAM",
    label: "Ogham",
    description: "The ancient Celtic tree alphabet.",
    icon: <IconFeather size={ICON_SIZE} />,
    entry: oghamGuide,
  },
  {
    key: "CLOSING",
    label: "Sin é!",
    description: "A note as we build this guide together.",
    icon: <IconHeart size={ICON_SIZE} />,
    entry: closingNote,
  },
]

export const findSection = (key: PageKey): LanguageSection | undefined =>
  LANGUAGE_SECTIONS.find((section) => section.key === key)
